import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Ban, Check, Copy, FileText, Pill as PillIcon, Plus, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { CATEGORY_LABELS, BULLETIN_SOURCE_LABELS, formatApiError, formatDate } from "@/lib/api";
import { Loading, Pill, StateBlock } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Panel, PanelHeader } from "./ui";
import { PAYMENT_METHODS, TREATMENT_TYPES } from "./PortalPatients";
import { PATIENT_WARDS, ageLabel, wardLabel, wardOption } from "@/lib/patient";
import { ConversationsPanel } from "./Conversations";
import { AttendanceIndicator } from "./MeetingGrid";
import { CareTeamFields } from "./StaffPicker";

const ALL_CATEGORIES = ["treatment_summary", "published_bulletins", "shareable_medications"];

const Section = ({ title, subtitle, children, testId }) => (
  <Panel testId={testId}>
    <PanelHeader label={subtitle} title={title} />
    <div className="mt-6">{children}</div>
  </Panel>
);

export default function PatientDetail() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [supportPeople, setSupportPeople] = useState([]);
  const [invite, setInvite] = useState(null);
  const [careOptions, setCareOptions] = useState(null);
  const [admissionDate, setAdmissionDate] = useState("");
  const [savingAdmission, setSavingAdmission] = useState(false);
  const [guardianName, setGuardianName] = useState("");
  const [savingGuardian, setSavingGuardian] = useState(false);
  const [fieldHistory, setFieldHistory] = useState([]);
  const [payment, setPayment] = useState({ payment_method: "undefined", health_plan_name: "" });
  const [savingPayment, setSavingPayment] = useState(false);
  const [confirmRemovePlan, setConfirmRemovePlan] = useState(false);
  const [treatment, setTreatment] = useState({ treatment_type: "", treatment_type_other: "" });
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [careTeam, setCareTeam] = useState({ counselor_id: null, psychologist_id: null, psychiatrist_id: null });
  const [savingCareTeam, setSavingCareTeam] = useState(false);
  const [expectedDischarge, setExpectedDischarge] = useState("");
  const [patientStatus, setPatientStatus] = useState("active");
  const [dischargeType, setDischargeType] = useState("");
  const [dischargeReason, setDischargeReason] = useState("");
  const [savingStay, setSavingStay] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [clinical, setClinical] = useState({});

  const [episodeForm, setEpisodeForm] = useState({ title: "", summary: "" });
  const [supportForm, setSupportForm] = useState({ name: "", email: "" });
  const [grantForm, setGrantForm] = useState({
    support_user_id: "",
    relationship: "",
    categories: [...ALL_CATEGORIES],
  });
  const [bulletinForm, setBulletinForm] = useState({
    title: "",
    body: "",
    source_type: "",
  });
  const [sourceFilter, setSourceFilter] = useState("");
  const [sourceEdit, setSourceEdit] = useState({});
  const [medForm, setMedForm] = useState({ name: "", dosage: "", schedule: "", shareable: true });
  const [eventForm, setEventForm] = useState({ medication_id: "", description: "" });

  const canManageAccess = ["clinical_coordinator", "admin"].includes(user?.role);
  const can = (key) => (user?.permissions || []).includes(key);
  const canEditPayment = can("patient_payment_edit");
  const canEditAdmission = can("patient_admission_edit");

  const load = async () => {
    try {
      const [{ data }, people, options, history] = await Promise.all([
        api.get(`/portal/patients/${patientId}`),
        api.get("/portal/support-people"),
        api.get("/portal/care-team/options"),
        api.get(`/portal/patients/${patientId}/field-history`),
      ]);
      api
        .get(`/portal/meetings/patients/${patientId}/discussions`)
        .then(({ data: list }) => setDiscussions(list))
        .catch(() => setDiscussions([]));
      setDetail(data);
      setSupportPeople(people.data);
      setCareOptions(options.data);
      setFieldHistory(history.data);
      setAdmissionDate(String(data.patient?.admission_date || "").slice(0, 10));
      setGuardianName(data.patient?.guardian_name || "");
      setExpectedDischarge(String(data.patient?.expected_discharge_date || "").slice(0, 10));
      setPatientStatus(data.patient?.status || "active");
      setClinical({
        ward: wardOption(data.patient?.ward),
        accommodation: data.patient?.accommodation || "",
        insurance_regional: data.patient?.insurance_regional || "",
        insurance_card: data.patient?.insurance_card || "",
        cid_primary: data.patient?.cid_primary || "",
        cids_secondary: (data.patient?.cids_secondary || []).join("; "),
        cid_hd_text: data.patient?.cid_hd_text || "",
        contact_phone: data.patient?.contact_phone || "",
      });
      setTreatment({
        treatment_type: data.patient?.treatment_type || "",
        treatment_type_other: data.patient?.treatment_type_other || "",
      });
      setPayment({
        payment_method: data.patient?.payment_method || "undefined",
        health_plan_name: data.patient?.health_plan_name || "",
      });
      const team = data.patient?.care_team || {};
      setCareTeam({
        counselor_id: team.counselor?.id || null,
        psychologist_id: team.psychologist?.id || null,
        psychiatrist_id: team.psychiatrist?.id || null,
      });
      if (data.medications?.length) {
        setEventForm((f) => ({ ...f, medication_id: f.medication_id || data.medications[0].id }));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const run = async (fn, successMessage) => {
    try {
      const result = await fn();
      if (successMessage) toast.success(successMessage);
      await load();
      return result;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    }
  };

  const savePayment = async () => {
    setSavingPayment(true);
    await run(
      () =>
        api.put(`/portal/patients/${patientId}`, {
          payment_method: payment.payment_method,
          health_plan_name: ["convenio", "liminar"].includes(payment.payment_method) ? payment.health_plan_name : "",
        }),
      "Meio de pagamento atualizado"
    );
    setSavingPayment(false);
  };

  if (!detail) return <Loading label="Carregando caso…" />;

  const { patient, episodes, bulletins, medications, medication_events: events, access_grants: grants } = detail;
  const becomingInactive = patientStatus === "discharged" && patient?.status !== "discharged";
  const dischargeIncomplete =
    becomingInactive &&
    (!dischargeType || (["pedida", "administrativa", "transferencia"].includes(dischargeType) && !dischargeReason.trim()));

  const episode = episodes[0];

  const toggleCategory = (c) =>
    setGrantForm((f) => ({
      ...f,
      categories: f.categories.includes(c) ? f.categories.filter((x) => x !== c) : [...f.categories, c],
    }));

  return (
    <div data-testid="patient-detail-page" className="space-y-8">
      <div>
        <Link
          to="/portal/pacientes"
          data-testid="back-to-patients"
          className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-600 hover:text-sage-700"
        >
          <ArrowLeft size={16} /> Pacientes
        </Link>
        <div className="mt-4">
          <PageHeader
            kicker="Caso clínico"
            titleTestId="patient-name"
            subtitleTestId="patient-cpf-masked"
            title={patient.full_name}
            subtitle={`CPF ${patient.cpf_masked} · ${ageLabel(patient)} · nascimento ${
              patient.birth_date || "—"
            } · Ala: ${wardLabel(patient)} · Meio de pagamento: ${
              patient.payment_method_label
            }${patient.health_plan_name ? ` · Plano de saúde: ${patient.health_plan_name}` : ""}`}
          />
          {user?.role === "admin" ? (
            <Button
              type="button"
              variant="outline"
              data-testid="patient-delete-open"
              onClick={() => {
                setDeleteReason("");
                setDeleteOpen(true);
              }}
              className="mt-4 border-danger/50 text-danger hover:bg-danger/10"
            >
              <Trash2 size={16} className="mr-2" aria-hidden="true" />
              Excluir paciente
            </Button>
          ) : null}
          {deleteOpen ? (
            <div
              data-testid="patient-delete-dialog"
              className="mt-4 space-y-3 rounded-2xl border border-danger/40 bg-danger/5 p-4"
            >
              <p className="text-[14px] font-semibold text-ink-800" data-testid="patient-delete-name">
                {patient.full_name}
              </p>
              <p className="text-[13.5px] text-ink-700">
                Deseja excluir este paciente cadastrado indevidamente? Ele deixará de aparecer nas listagens e nos
                indicadores do portal. Esta ação não representa uma alta.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="patient-delete-reason">Motivo da exclusão</Label>
                <Textarea
                  id="patient-delete-reason"
                  data-testid="patient-delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Descreva por que este cadastro foi criado indevidamente."
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="patient-delete-cancel"
                  onClick={() => setDeleteOpen(false)}
                >
                  Cancelar
                </Button>
                <Button
                  type="button"
                  data-testid="patient-delete-confirm"
                  disabled={!deleteReason.trim() || deleting}
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await api.delete(`/portal/patients/${patientId}`, { data: { reason: deleteReason.trim() } });
                      toast.success("Paciente excluído do portal.");
                      navigate("/portal/pacientes", { replace: true });
                    } catch (e) {
                      toast.error(formatApiError(e.response?.data?.detail));
                      setDeleting(false);
                    }
                  }}
                  className="bg-danger text-white hover:bg-danger/90"
                >
                  {deleting ? "Excluindo…" : "Confirmar exclusão"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
        {patient.birth_date_invalid ? (
          <p
            data-testid="patient-birth-invalid"
            className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-[13.5px] font-semibold text-amber-900"
          >
            Data de nascimento inválida no cadastro. Corrija o dado para que a idade seja calculada.
          </p>
        ) : null}
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        {/* Cadastro: data de internação */}
        <Section testId="admission-section" subtitle="Cadastro" title="Data de internação e responsável">
          <div className="space-y-5">
            {patient.admission_pending ? (
              <p
                data-testid="admission-pending-warning"
                className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3 text-[13.5px] font-semibold text-amber-900"
              >
                <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" /> Pendência: data de internação
                não informada. Estados que dependem dela ficam bloqueados.
              </p>
            ) : null}
            <div>
              <Label className="text-sm text-ink-600">Data de internação</Label>
              <Input
                type="date"
                data-testid="admission-date-input"
                value={admissionDate}
                onChange={(e) => setAdmissionDate(e.target.value)}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="admission-save"
                disabled={savingAdmission || !admissionDate}
                onClick={async () => {
                  setSavingAdmission(true);
                  await run(
                    () => api.put(`/portal/patients/${patientId}`, { admission_date: admissionDate }),
                    "Data de internação atualizada"
                  );
                  setSavingAdmission(false);
                }}
                className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
              >
                <Check size={16} className="mr-2" /> {savingAdmission ? "Salvando…" : "Salvar data"}
              </Button>
              <Link to={`/portal/linha-do-tempo/${patientId}`}>
                <Button variant="outline" data-testid="admission-open-timeline" className="h-11 rounded-full">
                  Abrir a linha do tempo
                </Button>
              </Link>
            </div>
            <div>
              <Label className="text-sm text-ink-600">Nome do responsável</Label>
              <Input
                data-testid="guardian-name-input"
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                className="mt-2 h-11 rounded-xl"
                placeholder="Nome de quem responde pelo paciente"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                data-testid="guardian-name-save"
                disabled={savingGuardian}
                onClick={async () => {
                  setSavingGuardian(true);
                  await run(
                    () => api.put(`/portal/patients/${patientId}`, { guardian_name: guardianName }),
                    "Nome do responsável atualizado"
                  );
                  setSavingGuardian(false);
                }}
                className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
              >
                <Check size={16} className="mr-2" /> {savingGuardian ? "Salvando…" : "Salvar responsável"}
              </Button>
            </div>
            {fieldHistory.some((h) => h.field === "guardian_name") ? (
              <ul data-testid="guardian-history" className="space-y-2 text-[13px] text-ink-600">
                {fieldHistory
                  .filter((h) => h.field === "guardian_name")
                  .map((h) => (
                    <li key={h.id} className="rounded-xl bg-sand/50 px-3.5 py-2.5">
                      {h.previous_value || "—"} → {h.new_value} · {formatDate(h.created_at)} ·{" "}
                      {h.changed_by_name || "—"}
                    </li>
                  ))}
              </ul>
            ) : null}
            {fieldHistory.length > 0 ? (
              <ul data-testid="admission-history" className="space-y-2 text-[13px] text-ink-600">
                {fieldHistory
                  .filter((h) => h.field === "admission_date")
                  .map((h) => (
                    <li key={h.id} className="rounded-xl bg-sand/50 px-3.5 py-2.5">
                      {h.previous_value || "—"} → {h.new_value} · {formatDate(h.created_at)} ·{" "}
                      {h.changed_by_name || "—"}
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        </Section>

        {/* Cadastro: foto, alta prevista e situação */}
        <Section testId="stay-section" subtitle="Cadastro" title="Foto, alta prevista e situação">
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              {patient.photo_thumb_url ? (
                <img
                  data-testid="patient-photo"
                  src={`${process.env.REACT_APP_BACKEND_URL}${patient.photo_thumb_url}`}
                  alt={`Foto de ${patient.full_name}`}
                  className="h-16 w-16 rounded-full object-cover"
                />
              ) : (
                <span
                  data-testid="patient-photo-placeholder"
                  className="grid h-16 w-16 place-items-center rounded-full bg-sage-50 text-[20px] font-semibold text-sage-700"
                >
                  {(patient.full_name || "?")
                    .split(" ")
                    .filter(Boolean)
                    .map((p, i, arr) => (i === 0 || i === arr.length - 1 ? p[0] : ""))
                    .join("")
                    .toUpperCase()}
                </span>
              )}
              <div className="flex flex-wrap gap-2.5">
                <label className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border border-border bg-white px-5 text-[14.5px] font-semibold text-ink-700 hover:bg-sand">
                  {uploadingPhoto ? "Enviando…" : "Enviar foto"}
                  <input
                    type="file"
                    accept="image/*"
                    data-testid="patient-photo-input"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const body = new FormData();
                      body.append("file", file, file.name || "foto.jpg");
                      setUploadingPhoto(true);
                      await run(() => api.post(`/portal/patients/${patientId}/photo`, body), "Foto atualizada");
                      setUploadingPhoto(false);
                    }}
                  />
                </label>
                {patient.photo_thumb_url ? (
                  <Button
                    variant="outline"
                    data-testid="patient-photo-remove"
                    className="h-11 rounded-full"
                    onClick={() => run(() => api.delete(`/portal/patients/${patientId}/photo`), "Foto removida")}
                  >
                    Remover foto
                  </Button>
                ) : null}
              </div>
            </div>

            <div>
              <Label className="text-sm text-ink-600">Data prevista de alta</Label>
              <Input
                type="date"
                data-testid="expected-discharge-input"
                value={expectedDischarge}
                onChange={(e) => setExpectedDischarge(e.target.value)}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm text-ink-600">Situação do paciente</Label>
              <select
                data-testid="patient-status-select"
                value={patientStatus}
                onChange={(e) => setPatientStatus(e.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                <option value="active">Ativo</option>
                <option value="discharged">Desligado (alta)</option>
              </select>
              <p className="mt-1.5 text-[13px] text-ink-500">
                Pacientes desligados saem da fila das Reuniões Clínicas.
              </p>
            </div>
            {becomingInactive ? (
              <div className="space-y-3 rounded-xl border border-amber-300 bg-amber-50/60 px-3.5 py-3">
                <div>
                  <Label className="text-sm text-ink-700">Tipo de alta *</Label>
                  <select
                    data-testid="discharge-type-select"
                    value={dischargeType}
                    onChange={(e) => setDischargeType(e.target.value)}
                    className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800"
                  >
                    <option value="">Selecione</option>
                    <option value="administrativa">Administrativa</option>
                    <option value="transferencia">Transferência</option>
                    <option value="medica">Médica</option>
                    <option value="pedida">Pedida</option>
                  </select>
                </div>
                {["pedida", "administrativa", "transferencia"].includes(dischargeType) ? (
                  <div>
                    <Label className="text-sm text-ink-700">Motivo da saída *</Label>
                    <textarea
                      data-testid="discharge-reason-input"
                      rows={3}
                      value={dischargeReason}
                      onChange={(e) => setDischargeReason(e.target.value)}
                      placeholder="Descreva o motivo da saída"
                      className="mt-2 w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-[14.5px] text-ink-800"
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
            {patient.status === "discharged" ? (
              <div data-testid="discharge-summary" className="rounded-xl bg-sand/60 px-3.5 py-3 text-[14px] text-ink-700">
                <p>Data de saída: {patient.discharge_date || "—"}</p>
                <p>Tipo de alta: {patient.discharge_type_label || "—"}</p>
                {patient.discharge_reason ? <p>Motivo da saída: {patient.discharge_reason}</p> : null}
                <p className="mt-1 text-[13px] text-ink-500">
                  Registrado por {patient.discharged_by_name || "—"}
                  {patient.discharged_at ? ` em ${new Date(patient.discharged_at).toLocaleString("pt-BR")}` : ""}
                </p>
              </div>
            ) : null}
            <Button
              data-testid="stay-save"
              disabled={savingStay || dischargeIncomplete}
              onClick={async () => {
                setSavingStay(true);
                await run(
                  () =>
                    api.put(`/portal/patients/${patientId}`, {
                      expected_discharge_date: expectedDischarge,
                      status: patientStatus,
                      ...(becomingInactive
                        ? { discharge_type: dischargeType, discharge_reason: dischargeReason }
                        : {}),
                    }),
                  "Cadastro atualizado"
                );
                setSavingStay(false);
              }}
              className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
            >
              <Check size={16} className="mr-2" /> {savingStay ? "Salvando…" : "Salvar"}
            </Button>
          </div>
        </Section>

        <AttendanceIndicator patientId={patient.id} />

        <ConversationsPanel patient={patient} />

        {/* Reuniões clínicas */}
        <Section testId="patient-meetings-section" subtitle="Reuniões clínicas" title="Discussões registradas">
          {discussions.length === 0 ? (
            <p data-testid="patient-meetings-empty" className="text-[14px] text-ink-500">
              Nenhuma discussão concluída em reunião clínica ainda.
            </p>
          ) : (
            <ol data-testid="patient-meetings-list" className="space-y-3">
              {discussions.map((d) => (
                <li key={d.id} className="rounded-xl bg-sand/50 px-4 py-3.5">
                  <p className="text-[14px] font-semibold text-ink-800">
                    Reunião de {formatDate(d.meeting_date)} · registrado por {d.author_name || "—"}
                  </p>
                  <p className="mt-1 text-[13.5px] text-ink-600">
                    Participantes: {(d.participants || []).map((p) => p.name).join(", ") || "—"}
                  </p>
                  {d.notes ? <p className="mt-1.5 text-[13.5px] text-ink-700">Observações: {d.notes}</p> : null}
                  <p className="mt-1 text-[13.5px] text-ink-700">
                    Definições: {d.decisions || (d.keep_current_conduct ? "Mantida a conduta atual" : "—")}
                  </p>
                  {d.flag_changes?.length ? (
                    <p className="mt-1 text-[13.5px] text-ink-600">
                      Marcações:{" "}
                      {d.flag_changes
                        .map((c) => `${c.label} (${c.action === "open" ? "aberta" : "encerrada"})`)
                        .join(", ")}
                    </p>
                  ) : null}
                  {d.reallocations?.length ? (
                    <p className="mt-1 text-[13.5px] text-ink-600">
                      Realocações:{" "}
                      {d.reallocations
                        .map((r) => `${r.role_label}: ${r.previous_name || "não definido"} → ${r.new_name || "não definido"}`)
                        .join(" · ")}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Section>

        {/* Cadastro: internação e CIDs */}
        <Section testId="clinical-data-section" subtitle="Cadastro" title="Internação e CIDs">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-sm text-ink-600">Ala</Label>
              <select
                data-testid="clinical-ward-select"
                disabled={!canEditAdmission}
                value={clinical.ward || ""}
                onChange={(e) => setClinical({ ...clinical, ward: e.target.value })}
                className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 disabled:bg-sand/60"
              >
                <option value="">Não informada</option>
                {PATIENT_WARDS.map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>
            {[
              ["Acomodação", "accommodation"],
              ["Regional do convênio", "insurance_regional"],
              ["Carteirinha", "insurance_card"],
              ["CID principal", "cid_primary"],
            ].map(([label, field]) => (
              <div key={field}>
                <Label className="text-sm text-ink-600">{label}</Label>
                <Input
                  data-testid={`clinical-${field}-input`}
                  disabled={!canEditAdmission}
                  value={clinical[field] || ""}
                  onChange={(e) => setClinical({ ...clinical, [field]: e.target.value })}
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            ))}
            <div>
              <Label className="text-sm text-ink-600">CIDs secundários</Label>
              <Input
                data-testid="clinical-cids-secondary-input"
                disabled={!canEditAdmission}
                value={clinical.cids_secondary || ""}
                onChange={(e) => setClinical({ ...clinical, cids_secondary: e.target.value })}
                placeholder="Separados por ponto e vírgula"
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm text-ink-600">CID / Hipótese diagnóstica (texto)</Label>
              <Input
                data-testid="clinical-cid-hd-input"
                disabled={!canEditAdmission}
                value={clinical.cid_hd_text || ""}
                onChange={(e) => setClinical({ ...clinical, cid_hd_text: e.target.value })}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-sm text-ink-600">Telefone de contato</Label>
              <Input
                data-testid="clinical-contact-phone-input"
                disabled={!canEditAdmission}
                value={clinical.contact_phone || ""}
                onChange={(e) => setClinical({ ...clinical, contact_phone: e.target.value })}
                className="mt-2 h-11 rounded-xl"
              />
            </div>
          </div>
          {canEditAdmission ? (
          <Button
            data-testid="clinical-save"
            className="mt-5 h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
            onClick={() =>
              run(
                () => {
                  const { ward, ...fields } = clinical;
                  const payload = {
                    ...fields,
                    cids_secondary: String(clinical.cids_secondary || "")
                      .split(";")
                      .map((c) => c.trim())
                      .filter(Boolean),
                  };
                  // A ala só vai no salvamento quando o usuário realmente a alterou.
                  if (ward && ward !== wardOption(detail?.patient?.ward)) payload.ward = ward;
                  return api.put(`/portal/patients/${patientId}`, payload);
                },
                "Dados de internação atualizados"
              )
            }
          >
            <Check size={16} className="mr-2" /> Salvar
          </Button>
          ) : (
            <p data-testid="clinical-readonly-note" className="mt-5 text-[13.5px] text-ink-500">
              Somente Administração e Diretoria podem alterar dados de internação e CIDs.
            </p>
          )}
        </Section>

        {/* Cadastro: tipo de tratamento */}
        <Section testId="treatment-section" subtitle="Cadastro" title="Tipo de tratamento">
          <div className="space-y-5">
            <p data-testid="treatment-current" className="text-[14px] leading-relaxed text-ink-600">
              Atual: <span className="font-semibold text-ink-800">{patient.treatment_type_label}</span>
              {patient.treatment_type_other ? (
                <>
                  {" · "}
                  <span data-testid="treatment-current-other" className="font-semibold text-ink-800">
                    {patient.treatment_type_other}
                  </span>
                </>
              ) : null}
            </p>
            <div>
              <Label className="text-sm text-ink-600">Tipo de tratamento</Label>
              <select
                data-testid="treatment-type-select"
                value={treatment.treatment_type}
                onChange={(e) =>
                  setTreatment({
                    treatment_type: e.target.value,
                    treatment_type_other: e.target.value === "OTHER" ? treatment.treatment_type_other : "",
                  })
                }
                className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                <option value="">Não informado</option>
                {TREATMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            {treatment.treatment_type === "OTHER" ? (
              <div>
                <Label className="text-sm text-ink-600">Especifique o tipo de tratamento (obrigatório)</Label>
                <Input
                  data-testid="treatment-other-input"
                  value={treatment.treatment_type_other}
                  onChange={(e) => setTreatment({ ...treatment, treatment_type_other: e.target.value })}
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            ) : null}
            <Button
              data-testid="treatment-save"
              disabled={savingTreatment}
              onClick={async () => {
                setSavingTreatment(true);
                await run(
                  () =>
                    api.put(`/portal/patients/${patientId}`, {
                      treatment_type: treatment.treatment_type,
                      treatment_type_other:
                        treatment.treatment_type === "OTHER" ? treatment.treatment_type_other : "",
                    }),
                  "Tipo de tratamento atualizado"
                );
                setSavingTreatment(false);
              }}
              className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
            >
              <Check size={16} className="mr-2" /> {savingTreatment ? "Salvando…" : "Salvar tipo de tratamento"}
            </Button>
          </div>
        </Section>

        {/* Cadastro: meio de pagamento */}
        <Section testId="payment-section" subtitle="Cadastro" title="Meio de pagamento">
          <div className="space-y-5">
            <p data-testid="payment-current" className="text-[14px] leading-relaxed text-ink-600">
              Atual: <span className="font-semibold text-ink-800">{patient.payment_method_label}</span>
              {patient.health_plan_name ? (
                <>
                  {" "}
                  · Plano de saúde:{" "}
                  <span data-testid="payment-current-plan" className="font-semibold text-ink-800">
                    {patient.health_plan_name}
                  </span>
                </>
              ) : null}
            </p>
            {!canEditPayment ? (
              <p data-testid="payment-readonly-note" className="text-[13.5px] text-ink-500">
                Somente Administração e Diretoria podem alterar o meio de pagamento e os dados do convênio.
              </p>
            ) : null}
            <div className={canEditPayment ? "" : "hidden"}>
              <Label className="text-sm text-ink-600">Meio de pagamento</Label>
              <select
                data-testid="payment-method-select"
                value={payment.payment_method}
                onChange={(e) =>
                  setPayment({
                    payment_method: e.target.value,
                    health_plan_name: ["convenio", "liminar"].includes(e.target.value) ? payment.health_plan_name : "",
                  })
                }
                className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14.5px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15"
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            {["convenio", "liminar"].includes(payment.payment_method) ? (
              <div>
                <Label className="text-sm text-ink-600">
                  {payment.payment_method === "liminar" ? "Plano de saúde (opcional)" : "Plano de saúde (obrigatório)"}
                </Label>
                <Input
                  data-testid="payment-health-plan-input"
                  value={payment.health_plan_name}
                  onChange={(e) => setPayment({ ...payment, health_plan_name: e.target.value })}
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
            ) : null}
            {confirmRemovePlan ? (
              <div
                data-testid="payment-remove-plan-confirm"
                className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-3"
              >
                <p className="text-[13.5px] font-semibold text-amber-900">
                  O paciente deixará de usar Convênio. O vínculo com o plano “{patient.health_plan_name}” será
                  removido. Confirmar?
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    data-testid="payment-remove-plan-confirm-yes"
                    onClick={async () => {
                      setConfirmRemovePlan(false);
                      await savePayment();
                    }}
                    className="h-10 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
                  >
                    Sim, remover o plano
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="payment-remove-plan-confirm-no"
                    onClick={() => {
                      setConfirmRemovePlan(false);
                      setPayment({
                        payment_method: patient.payment_method,
                        health_plan_name: patient.health_plan_name || "",
                      });
                    }}
                    className="h-10 rounded-full"
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : !canEditPayment ? null : (
              <Button
                data-testid="payment-save"
                disabled={savingPayment}
                onClick={() => {
                  if (
                    patient.payment_method === "convenio" &&
                    !["convenio", "liminar"].includes(payment.payment_method) &&
                    patient.health_plan_name
                  ) {
                    setConfirmRemovePlan(true);
                    return;
                  }
                  savePayment();
                }}
                className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
              >
                <Check size={16} className="mr-2" /> {savingPayment ? "Salvando…" : "Salvar meio de pagamento"}
              </Button>
            )}
          </div>
        </Section>

        {/* Equipe responsável */}
        <Section
          testId="care-team-section"
          subtitle="Equipe responsável"
          title="Conselheiro, psicólogo e psiquiatra"
        >
          <div className="space-y-5">
            <p className="text-[14px] leading-relaxed text-ink-600">
              Cada campo aceita um único funcionário ativo do cargo correspondente. Deixe em branco para definir
              depois.
            </p>
            <CareTeamFields
              options={careOptions}
              values={careTeam}
              onChange={setCareTeam}
              testPrefix="patient-care-team"
              disabled={savingCareTeam}
            />
            <Button
              data-testid="care-team-save"
              disabled={savingCareTeam}
              onClick={async () => {
                setSavingCareTeam(true);
                await run(
                  () => api.put(`/portal/patients/${patientId}/care-team`, careTeam),
                  "Equipe responsável atualizada"
                );
                setSavingCareTeam(false);
              }}
              className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700"
            >
              <Check size={16} className="mr-2" /> {savingCareTeam ? "Salvando…" : "Salvar equipe responsável"}
            </Button>
          </div>
        </Section>

        {/* Episódio */}
        <Section testId="episode-section" subtitle="Etapa 3" title="Episódio de tratamento">
          {episode ? (
            <div data-testid="episode-card" className="rounded-xl bg-sand/60 p-5">
              <h3 className="font-display text-base font-medium text-ink-900">{episode.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-600">{episode.summary}</p>
              <p className="mt-3 text-xs text-ink-400">Início em {formatDate(episode.started_at)}</p>
            </div>
          ) : (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                run(() => api.post(`/portal/patients/${patientId}/episodes`, episodeForm), "Episódio criado");
              }}
            >
              <div>
                <Label className="text-sm text-ink-600">Título</Label>
                <Input
                  data-testid="episode-title-input"
                  value={episodeForm.title}
                  onChange={(e) => setEpisodeForm({ ...episodeForm, title: e.target.value })}
                  className="mt-2 h-11 rounded-xl"
                  required
                />
              </div>
              <div>
                <Label className="text-sm text-ink-600">Resumo do tratamento</Label>
                <Textarea
                  data-testid="episode-summary-input"
                  value={episodeForm.summary}
                  onChange={(e) => setEpisodeForm({ ...episodeForm, summary: e.target.value })}
                  className="mt-2 rounded-xl"
                  rows={3}
                  required
                />
              </div>
              <Button data-testid="episode-create-submit" className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700">
                <Plus size={16} className="mr-2" /> Criar episódio
              </Button>
            </form>
          )}
        </Section>

        {/* Rede de apoio + vínculo */}
        <Section testId="support-section" subtitle="Etapas 4 a 6" title="Rede de apoio e vínculo">
          {!canManageAccess ? (
            <StateBlock
              testId="support-permission-block"
              icon={Ban}
              title="Sem permissão"
              description="Apenas coordenação clínica e administração podem criar vínculos e convites."
            />
          ) : (
            <div className="space-y-6">
              <form
                className="grid gap-4 sm:grid-cols-2"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const res = await run(() => api.post("/portal/support-people", supportForm), "Pessoa cadastrada");
                  if (res?.data) setGrantForm((f) => ({ ...f, support_user_id: res.data.id }));
                }}
              >
                <div>
                  <Label className="text-sm text-ink-600">Nome</Label>
                  <Input
                    data-testid="support-name-input"
                    value={supportForm.name}
                    onChange={(e) => setSupportForm({ ...supportForm, name: e.target.value })}
                    className="mt-2 h-11 rounded-xl"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm text-ink-600">E-mail</Label>
                  <Input
                    data-testid="support-email-input"
                    type="email"
                    value={supportForm.email}
                    onChange={(e) => setSupportForm({ ...supportForm, email: e.target.value })}
                    className="mt-2 h-11 rounded-xl"
                    required
                  />
                </div>
                <Button
                  data-testid="support-create-submit"
                  className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700 sm:col-span-2"
                >
                  <UserPlus size={16} className="mr-2" /> Cadastrar pessoa da rede de apoio
                </Button>
              </form>

              <form
                className="space-y-4 border-t border-border pt-6"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const res = await run(
                    () =>
                      api.post("/portal/access-grants", {
                        patient_id: patientId,
                        episode_id: episode?.id || null,
                        support_user_id: grantForm.support_user_id,
                        relationship: grantForm.relationship,
                        categories: grantForm.categories,
                      }),
                    "Vínculo criado e convite gerado"
                  );
                  if (res?.data?.invite) setInvite(res.data.invite);
                }}
              >
                <div>
                  <Label className="text-sm text-ink-600">Pessoa</Label>
                  <select
                    data-testid="grant-support-select"
                    value={grantForm.support_user_id}
                    onChange={(e) => setGrantForm({ ...grantForm, support_user_id: e.target.value })}
                    className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                    required
                  >
                    <option value="">Selecione…</option>
                    {supportPeople.map((p) => (
                      <option key={p.id} value={p.id}>{`${p.name} — ${p.email}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-sm text-ink-600">Relação</Label>
                  <Input
                    data-testid="grant-relationship-input"
                    value={grantForm.relationship}
                    onChange={(e) => setGrantForm({ ...grantForm, relationship: e.target.value })}
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div className="space-y-3">
                  <Label className="text-sm text-ink-600">Categorias autorizadas</Label>
                  {ALL_CATEGORIES.map((c) => (
                    <label key={c} className="flex items-center gap-3 text-sm text-ink-600">
                      <Checkbox
                        data-testid={`grant-category-${c}`}
                        checked={grantForm.categories.includes(c)}
                        onCheckedChange={() => toggleCategory(c)}
                      />
                      {CATEGORY_LABELS[c]}
                    </label>
                  ))}
                </div>
                <Button
                  data-testid="grant-create-submit"
                  className="h-11 w-full rounded-full bg-sage-600 text-white hover:bg-sage-700"
                >
                  Criar vínculo e gerar convite
                </Button>
              </form>

              {invite ? (
                <div data-testid="invite-preview" className="rounded-xl border border-warning/40 bg-warning/10 p-5">
                  <p className="er-label text-[#8A6216]">Convite gerado</p>
                  <p className="mt-2 text-sm text-ink-600">
                    O envio de e-mail será feito pela equipe. Convite de uso único, expira em{" "}
                    {formatDate(invite.expires_at)}, vinculado a <strong>{invite.email}</strong>.
                  </p>
                  <code
                    data-testid="invite-link"
                    className="mt-3 block break-all rounded-lg bg-white px-3 py-2 text-xs text-ink-700"
                  >
                    {window.location.origin + invite.preview_link}
                  </code>
                  <div className="mt-4 flex flex-wrap gap-3">
                    <Button
                      variant="outline"
                      data-testid="invite-copy"
                      onClick={() => {
                        navigator.clipboard?.writeText(window.location.origin + invite.preview_link);
                        toast.success("Link copiado");
                      }}
                      className="rounded-full"
                    >
                      <Copy size={15} className="mr-2" /> Copiar link
                    </Button>
                    <a
                      href={invite.preview_link}
                      data-testid="invite-open"
                      className="inline-flex items-center rounded-full bg-sage-600 px-5 py-2 text-sm text-bone hover:bg-sage-700"
                    >
                      Abrir ativação
                    </a>
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </Section>

        {/* Boletins */}
        {can("bulletins") ? (
        <Section testId="bulletins-section" subtitle="Etapa 8" title="Boletins">
          {episode ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (!bulletinForm.source_type) {
                  toast.error("Selecione a fonte do boletim para salvar");
                  return;
                }
                run(
                  () => api.post(`/portal/patients/${patientId}/bulletins`, { ...bulletinForm, episode_id: episode.id }),
                  "Boletim criado como rascunho"
                );
              }}
            >
              <Input
                data-testid="bulletin-title-input"
                value={bulletinForm.title}
                onChange={(e) => setBulletinForm({ ...bulletinForm, title: e.target.value })}
                className="h-11 rounded-xl"
                placeholder="Título do boletim"
                required
              />
              <div>
                <Label className="text-sm text-ink-600">Fonte do boletim *</Label>
                <select
                  data-testid="bulletin-source-select"
                  value={bulletinForm.source_type}
                  onChange={(e) => setBulletinForm({ ...bulletinForm, source_type: e.target.value })}
                  className="mt-2 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                  required
                >
                  <option value="">Selecione a fonte…</option>
                  {Object.entries(BULLETIN_SOURCE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {!bulletinForm.source_type ? (
                  <p data-testid="bulletin-source-hint" className="mt-2 text-xs text-danger">
                    Obrigatório: escolha Conselheiro(a), Psicólogo(a) ou Psiquiatra.
                  </p>
                ) : null}
              </div>
              <Textarea
                data-testid="bulletin-body-input"
                value={bulletinForm.body}
                onChange={(e) => setBulletinForm({ ...bulletinForm, body: e.target.value })}
                className="rounded-xl"
                rows={3}
                required
              />
              <div data-testid="bulletin-preview" className="rounded-xl bg-sand/60 p-4">
                <p className="er-label">Prévia</p>
                <p className="mt-2 font-display text-base font-medium text-ink-900">{bulletinForm.title}</p>
                <p className="mt-1 text-xs font-semibold text-sage-700">
                  Fonte: {BULLETIN_SOURCE_LABELS[bulletinForm.source_type] || "não selecionada"}
                </p>
                <p className="mt-2 text-sm text-ink-600">{bulletinForm.body}</p>
              </div>
              <Button data-testid="bulletin-create-submit" className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700">
                <FileText size={16} className="mr-2" /> Criar rascunho
              </Button>
            </form>
          ) : (
            <StateBlock testId="bulletin-needs-episode" icon={FileText} title="Crie o episódio primeiro" />
          )}

          <div className="mt-6">
            <Label className="text-xs text-ink-500">Filtrar por fonte</Label>
            <select
              data-testid="bulletin-source-filter"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="mt-2 h-10 w-full rounded-xl border border-border bg-white px-3 text-sm"
            >
              <option value="">Todas as fontes</option>
              {Object.entries(BULLETIN_SOURCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6 space-y-3">
            {bulletins.filter((b) => !sourceFilter || b.source_type === sourceFilter).length === 0 ? (
              <p className="text-sm text-ink-400">Nenhum boletim com esse filtro.</p>
            ) : (
              bulletins
                .filter((b) => !sourceFilter || b.source_type === sourceFilter)
                .map((b) => (
                <div key={b.id} data-testid={`bulletin-row-${b.id}`} className="rounded-xl border border-border p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h4 className="font-medium text-ink-800">{b.title}</h4>
                    <Pill tone={b.status === "published" ? "success" : b.status === "draft" ? "warning" : "neutral"} testId={`bulletin-status-${b.id}`}>
                      {b.status}
                    </Pill>
                  </div>
                  <p data-testid={`bulletin-source-${b.id}`} className="mt-1 text-xs font-semibold text-sage-700">
                    Fonte: {b.source_label || BULLETIN_SOURCE_LABELS[b.source_type] || "não classificada"}
                  </p>
                  <p className="mt-2 text-sm text-ink-500">{b.body}</p>
                  {b.correction_notice ? (
                    <p className="mt-2 rounded-lg bg-warning/15 px-3 py-2 text-xs text-ink-700">{b.correction_notice}</p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <select
                      data-testid={`bulletin-source-change-${b.id}`}
                      value={sourceEdit[b.id]?.source_type || b.source_type || ""}
                      onChange={(e) => setSourceEdit({ ...sourceEdit, [b.id]: { ...sourceEdit[b.id], source_type: e.target.value } })}
                      className="h-9 rounded-xl border border-border bg-white px-2 text-xs"
                    >
                      {Object.entries(BULLETIN_SOURCE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                    {["published", "corrected"].includes(b.status) ? (
                      <Input
                        data-testid={`bulletin-source-reason-${b.id}`}
                        value={sourceEdit[b.id]?.reason || ""}
                        onChange={(e) => setSourceEdit({ ...sourceEdit, [b.id]: { ...sourceEdit[b.id], reason: e.target.value } })}
                        placeholder="Motivo da correção"
                        className="h-9 w-48 rounded-xl text-xs"
                      />
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`bulletin-source-save-${b.id}`}
                      onClick={() =>
                        run(
                          () =>
                            api.post(`/portal/bulletins/${b.id}/source`, {
                              source_type: sourceEdit[b.id]?.source_type || b.source_type,
                              reason: sourceEdit[b.id]?.reason || null,
                            }),
                          "Fonte atualizada"
                        )
                      }
                      className="rounded-full"
                    >
                      Salvar fonte
                    </Button>
                    {b.status === "draft" ? (
                      <Button
                        size="sm"
                        data-testid={`bulletin-publish-${b.id}`}
                        onClick={() =>
                          run(() => api.post(`/portal/bulletins/${b.id}/transition`, null, { params: { status: "published" } }), "Boletim publicado")
                        }
                        className="rounded-full bg-success text-white hover:bg-success/90"
                      >
                        <Check size={15} className="mr-1" /> Publicar
                      </Button>
                    ) : null}
                    {b.status === "published" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`bulletin-correct-${b.id}`}
                          onClick={() =>
                            run(() => api.post(`/portal/bulletins/${b.id}/transition`, null, { params: { status: "corrected" } }), "Boletim corrigido")
                          }
                          className="rounded-full"
                        >
                          Corrigir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`bulletin-archive-${b.id}`}
                          onClick={() =>
                            run(() => api.post(`/portal/bulletins/${b.id}/transition`, null, { params: { status: "archived" } }), "Boletim arquivado")
                          }
                          className="rounded-full"
                        >
                          Arquivar
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))
            )}
          </div>
        </Section>
        ) : null}

        {/* Medicação */}
        {can("medications") ? (
        <Section testId="medications-section" subtitle="Etapa 9" title="Medicamentos e alterações">
          {episode ? (
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                run(
                  () => api.post(`/portal/patients/${patientId}/medications`, { ...medForm, episode_id: episode.id }),
                  "Medicamento cadastrado"
                );
              }}
            >
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  data-testid="medication-name-input"
                  value={medForm.name}
                  onChange={(e) => setMedForm({ ...medForm, name: e.target.value })}
                  className="h-11 rounded-xl"
                  placeholder="Nome"
                  required
                />
                <Input
                  data-testid="medication-dosage-input"
                  value={medForm.dosage}
                  onChange={(e) => setMedForm({ ...medForm, dosage: e.target.value })}
                  className="h-11 rounded-xl"
                  placeholder="Dose"
                  required
                />
                <Input
                  data-testid="medication-schedule-input"
                  value={medForm.schedule}
                  onChange={(e) => setMedForm({ ...medForm, schedule: e.target.value })}
                  className="h-11 rounded-xl"
                  placeholder="Horário"
                  required
                />
              </div>
              <label className="flex items-center gap-3 text-sm text-ink-600">
                <Checkbox
                  data-testid="medication-shareable-checkbox"
                  checked={medForm.shareable}
                  onCheckedChange={(v) => setMedForm({ ...medForm, shareable: !!v })}
                />
                Compartilhável com a rede de apoio
              </label>
              <Button data-testid="medication-create-submit" className="h-11 rounded-full bg-sage-600 text-bone hover:bg-sage-700">
                <PillIcon size={16} className="mr-2" /> Cadastrar medicamento
              </Button>
            </form>
          ) : (
            <StateBlock testId="medication-needs-episode" icon={PillIcon} title="Crie o episódio primeiro" />
          )}

          {medications.length > 0 ? (
            <form
              className="mt-6 space-y-4 border-t border-border pt-6"
              onSubmit={(e) => {
                e.preventDefault();
                run(
                  () =>
                    api.post(`/portal/medications/${eventForm.medication_id}/events`, {
                      description: eventForm.description,
                      event_type: "dose_change",
                      shareable: true,
                    }),
                  "Alteração registrada e compartilhada"
                );
              }}
            >
              <Label className="text-sm text-ink-600">Registrar alteração de dose</Label>
              <select
                data-testid="event-medication-select"
                value={eventForm.medication_id}
                onChange={(e) => setEventForm({ ...eventForm, medication_id: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                required
              >
                {medications.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} — {m.dosage}
                  </option>
                ))}
              </select>
              <Input
                data-testid="event-description-input"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                className="h-11 rounded-xl"
                required
              />
              <Button data-testid="event-create-submit" className="h-11 rounded-full bg-sage-600 text-white hover:bg-sage-700">
                Registrar alteração compartilhável
              </Button>
            </form>
          ) : null}

          <div className="mt-6 space-y-2">
            {medications.map((m) => (
              <div key={m.id} data-testid={`medication-row-${m.id}`} className="flex items-center justify-between rounded-xl bg-sand/60 px-4 py-3 text-sm">
                <span className="text-ink-700">
                  {m.name} · {m.dosage} · {m.schedule}
                </span>
                <Pill tone={m.shareable ? "success" : "neutral"}>{m.shareable ? "Compartilhável" : "Interno"}</Pill>
              </div>
            ))}
            {events.map((ev) => (
              <p key={ev.id} data-testid={`medication-event-${ev.id}`} className="text-xs text-ink-500">
                {formatDate(ev.created_at)} — {ev.description}
              </p>
            ))}
          </div>
        </Section>
        ) : null}
      </div>

      {/* Vínculos do paciente */}
      <Section testId="patient-grants-section" subtitle="Etapa 12" title="Vínculos deste paciente">
        {grants.length === 0 ? (
          <p className="text-sm text-ink-400">Nenhum vínculo criado.</p>
        ) : (
          <div className="space-y-3">
            {grants.map((g) => (
              <div key={g.id} data-testid={`patient-grant-${g.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border p-4">
                <div>
                  <p className="font-medium text-ink-800">
                    {g.support_name} <span className="text-sm text-ink-400">· {g.support_email}</span>
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(g.categories || []).map((c) => (
                      <Pill key={c}>{CATEGORY_LABELS[c] || c}</Pill>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Pill tone={g.status === "active" ? "success" : "danger"}>{g.status === "active" ? "Ativo" : "Revogado"}</Pill>
                  {g.status === "active" && canManageAccess ? (
                    <Button
                      variant="outline"
                      data-testid={`patient-grant-revoke-${g.id}`}
                      onClick={() => run(() => api.post(`/portal/access-grants/${g.id}/revoke`), "Vínculo revogado")}
                      className="rounded-full border-danger/40 text-danger hover:bg-danger/10 hover:text-danger"
                    >
                      <Ban size={15} className="mr-2" /> Revogar
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
